import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, FileText } from 'lucide-react';
import { useIFCarteiraResumo } from '../hooks/useIFCarteira';
import { useComprovacaoContratoMap } from '../hooks/useComprovacaoContratoMap';
import { useComprovacaoFinanceiroResumo } from '../hooks/useComprovacaoFinanceiroResumo';
import { ComprovacaoStatusBadge } from '../components/ComprovacaoStatusBadge';
import { IFExecutiveSummary } from '../components/IFExecutiveSummary';
import { EmpMedicoesView } from '@/components/empreendimentos/EmpMedicoesView';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import { EmptyState } from '@/components/system/EmptyState';
import { FilterBar } from '@/components/system/FilterBar';
import { DataTable, type Column } from '@/components/system/DataTable';
import { Badge } from '@/components/ui/badge';
import type { IFContratoResumo } from '../types/financiamento.types';

const IFMedicoesPage: React.FC = () => {
  const navigate = useNavigate();
  const { resumos, isLoading } = useIFCarteiraResumo();
  const [selectedContratoId, setSelectedContratoId] = useState<string | null>(null);

  const contratoIds = useMemo(() => resumos.map(r => r.contrato_id), [resumos]);
  const { data: comprovacaoMap = {} } = useComprovacaoContratoMap(contratoIds);
  const { data: financeiroMap = {} } = useComprovacaoFinanceiroResumo(contratoIds);

  // Ordenação default: pior atraso de comprovação no topo
  const resumosOrdenados = useMemo(() => {
    const statusRank: Record<string, number> = { critico: 3, atencao: 2, saudavel: 1 };
    return [...resumos].sort((a, b) => {
      const ca = comprovacaoMap[a.contrato_id];
      const cb = comprovacaoMap[b.contrato_id];
      const rankA = ca ? statusRank[ca.status_risco] ?? 0 : 0;
      const rankB = cb ? statusRank[cb.status_risco] ?? 0 : 0;
      if (rankA !== rankB) return rankB - rankA;
      const piorA = ca?.pior_atraso_dias ?? 0;
      const piorB = cb?.pior_atraso_dias ?? 0;
      return piorB - piorA;
    });
  }, [resumos, comprovacaoMap]);

  if (isLoading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  const selectedContrato = resumos.find(r => r.contrato_id === selectedContratoId);

  // Colunas reordenadas: Risco de Pagamento sobe para a 3ª posição
  const summaryColumns: Column<IFContratoResumo>[] = [
    { key: 'contrato', label: 'Contrato', render: r => <span className="font-medium">{r.contrato_nome}</span> },
    { key: 'contratada', label: 'Contratada', render: r => <span className="text-sm text-muted-foreground">{r.contratada}</span> },
    { key: 'risco_pagamento', label: 'Risco de Pagamento', render: r => <ComprovacaoStatusBadge resumo={comprovacaoMap[r.contrato_id]} /> },
    { key: 'ultima_medicao', label: 'Última Medição', align: 'center', render: r => r.ultimo_boletim_numero ? <Badge variant="outline" className="text-xs">#{r.ultimo_boletim_numero}</Badge> : <span>—</span> },
    { key: 'avanco_fisico', label: 'Avanço Físico', align: 'center', render: r => <span className="text-sm font-medium">{r.progresso_fisico_pct.toFixed(1)}%</span> },
  ];

  return (
    <div className="space-y-6">
        <PageHeader
          title="Medições e Execução"
          subtitle="Visão consolidada de exposição e comprovação por contrato — somente leitura"
        />

        {!selectedContratoId && (
          <IFExecutiveSummary
            comprovacaoMap={comprovacaoMap}
            financeiroMap={financeiroMap}
          />
        )}

        <FilterBar>
          <label className="text-sm font-medium text-muted-foreground whitespace-nowrap">Contrato:</label>
          <Select value={selectedContratoId || ''} onValueChange={v => setSelectedContratoId(v)}>
            <SelectTrigger className="max-w-md h-8 text-sm">
              <SelectValue placeholder="Escolha um contrato para detalhar" />
            </SelectTrigger>
            <SelectContent>
              {resumos.map(r => (
                <SelectItem key={r.contrato_id} value={r.contrato_id}>
                  {r.contrato_nome} — {r.contratada}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterBar>

        {!selectedContratoId ? (
          <SectionCard title="Resumo de Medições" icon={FileText}>
            {resumos.length === 0 ? (
              <EmptyState text="Nenhum contrato vinculado à carteira de monitoramento." icon={FileText} />
            ) : (
              <>
                <DataTable
                  columns={summaryColumns}
                  data={resumosOrdenados}
                  keyExtractor={r => r.contrato_id}
                  emptyText="Nenhum contrato disponível."
                  onRowClick={r => setSelectedContratoId(r.contrato_id)}
                  rowClassName={r => {
                    const status = comprovacaoMap[r.contrato_id]?.status_risco;
                    return status === 'critico' ? 'bg-destructive/[0.04]' : undefined;
                  }}
                />
                <p className="mt-3 text-xs text-muted-foreground">
                  🟢 Em dia · 🟡 Atenção (1–30 dias sem comprovante) · 🔴 Crítico (&gt;30 dias sem comprovante)
                </p>
              </>
            )}
          </SectionCard>
        ) : (
          <EmpMedicoesView
            contratoId={selectedContratoId}
            contratoNome={selectedContrato?.contrato_nome || ''}
            canEdit={false}
          />
        )}
    </div>
  );
};

export default IFMedicoesPage;
